import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    Box,
    Typography,
    Button,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Checkbox,
    FormControlLabel,
} from "@mui/material";
import { authHeader } from "../auth";
import { usePresencePing } from "../hooks/usePresencePing";

const expectedFields = ["fiscode", "lastName", "firstName", "birthyear", "gender", "nation", "club"];
const MAPPING_STORAGE_KEY = "athletesImportMapping";

// --- Parsing CSV robuste : BOM, fins de ligne CRLF/LF, délimiteur variable,
// champs entre guillemets. Pas de dépendance externe, gère les cas réels
// rencontrés (exports FIS/SkiAlpPro : souvent ";" ou tabulation, sans en-tête). ---

function stripBOM(text) {
    return text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
}

function detectDelimiter(sampleLine) {
    const counts = {
        ",": (sampleLine.match(/,/g) || []).length,
        ";": (sampleLine.match(/;/g) || []).length,
        "\t": (sampleLine.match(/\t/g) || []).length,
    };
    const best = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
    return best && best[1] > 0 ? best[0] : ",";
}

function parseCsvLine(line, delimiter) {
    const result = [];
    let cur = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (inQuotes) {
            if (ch === '"') {
                if (line[i + 1] === '"') {
                    cur += '"';
                    i++;
                } else {
                    inQuotes = false;
                }
            } else {
                cur += ch;
            }
        } else if (ch === '"') {
            inQuotes = true;
        } else if (ch === delimiter) {
            result.push(cur);
            cur = "";
        } else {
            cur += ch;
        }
    }
    result.push(cur);
    return result;
}

function parseCsv(text, delimiter) {
    const clean = stripBOM(text).replace(/\r\n/g, "\n").replace(/\r/g, "\n");
    const lines = clean.split("\n").filter((l) => l.trim() !== "");
    return lines.map((l) => parseCsvLine(l, delimiter));
}

export default function AthletesImportPage() {
    const { category } = useParams();
    const navigate = useNavigate();
    usePresencePing(category, "Import athlètes");

    const [rows2D, setRows2D] = useState([]);
    const [delimiter, setDelimiter] = useState(",");
    const [hasHeader, setHasHeader] = useState(false);
    const [ignoreRows, setIgnoreRows] = useState(0);
    const [mapping, setMapping] = useState({});
    const [mappedData, setMappedData] = useState([]);
    const [athletes, setAthletes] = useState([]);
    const [error, setError] = useState("");

    // Le mapping des colonnes est réutilisé d'une importation à l'autre
    // (même format de fichier en général) — on le charge une fois au
    // montage, et on le sauvegarde à chaque changement.
    useEffect(() => {
        try {
            const saved = JSON.parse(localStorage.getItem(MAPPING_STORAGE_KEY) || "null");
            if (saved) {
                setMapping(saved.mapping || {});
                setDelimiter(saved.delimiter || ",");
                setHasHeader(saved.hasHeader || false);
                setIgnoreRows(saved.ignoreRows || 0);
            }
        } catch {
            /* ignore un éventuel contenu corrompu */
        }
    }, []);

    useEffect(() => {
        localStorage.setItem(
            MAPPING_STORAGE_KEY,
            JSON.stringify({ mapping, delimiter, hasHeader, ignoreRows })
        );
    }, [mapping, delimiter, hasHeader, ignoreRows]);

    const handleFileSelect = async (file) => {
        setError("");
        const text = await file.text();
        const firstLine = stripBOM(text).split(/\r?\n/)[0] || "";
        const guessDelim = detectDelimiter(firstLine);

        setDelimiter(guessDelim);
        setRows2D(parseCsv(text, guessDelim));
        setMappedData([]);
        // Le mapping N'EST PLUS remis à zéro ici : on le garde (chargé
        // depuis localStorage), pratique si le fichier suivant a la même
        // structure de colonnes.
    };

    const changeDelimiter = (newDelimiter, rawText) => {
        setDelimiter(newDelimiter);
        setRows2D(parseCsv(rawText, newDelimiter));
    };

    const headerLabels = hasHeader
        ? (rows2D[ignoreRows] || []).map((v, i) => `${v || "(vide)"} [col ${i + 1}]`)
        : (rows2D[0] || []).map((_, i) => `Colonne ${i + 1}`);

    const dataRows = hasHeader ? rows2D.slice(ignoreRows + 1) : rows2D;

    const applyMapping = () => {
        const mapped = dataRows.map((row) => {
            const obj = {};
            expectedFields.forEach((field) => {
                const colIndex = mapping[field];
                let v = colIndex !== undefined && colIndex !== "" ? row[Number(colIndex)] : null;
                if (v === "") v = null;
                obj[field] = v;
            });
            obj.name = [obj.firstName, obj.lastName].filter(Boolean).join(" ");
            return obj;
        });
        setMappedData(mapped);
    };

    const sendToBackend = async () => {
        setError("");
        try {
            const res = await fetch(`/api/athletes/${category}/import`, {
                method: "POST",
                headers: { "Content-Type": "application/json", ...authHeader() },
                body: JSON.stringify(mappedData),
            });

            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                setError(data.error || `HTTP ${res.status}`);
                return;
            }

            const data = await res.json();
            setAthletes(data.athletes || []);
            navigate("/parallel");
        } catch (err) {
            setError(err.message);
        }
    };

    return (
        <Box p={3}>
            <Typography variant="h5" gutterBottom>
                Import athlètes (CSV) — {category}
            </Typography>

            <input
                type="file"
                accept=".csv,.txt"
                onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) handleFileSelect(file);
                }}
            />

            {rows2D.length > 0 && (
                <Box sx={{ mt: 3 }}>
                    <Box sx={{ display: "flex", gap: 3, alignItems: "center", mb: 2, flexWrap: "wrap" }}>
                        <FormControl size="small" sx={{ minWidth: 160 }}>
                            <InputLabel>Séparateur</InputLabel>
                            <Select
                                label="Séparateur"
                                value={delimiter}
                                onChange={(e) => {
                                    const rawText = rows2D.map((r) => r.join(delimiter)).join("\n");
                                    changeDelimiter(e.target.value, rawText);
                                }}
                            >
                                <MenuItem value=",">Virgule (,)</MenuItem>
                                <MenuItem value=";">Point-virgule (;)</MenuItem>
                                <MenuItem value={"\t"}>Tabulation</MenuItem>
                            </Select>
                        </FormControl>

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={hasHeader}
                                    onChange={(e) => setHasHeader(e.target.checked)}
                                />
                            }
                            label="Le fichier a une ligne d'en-tête"
                        />

                        {hasHeader && (
                            <FormControl size="small" sx={{ minWidth: 160 }}>
                                <InputLabel>Ligne d'en-tête</InputLabel>
                                <Select
                                    label="Ligne d'en-tête"
                                    value={ignoreRows}
                                    onChange={(e) => setIgnoreRows(Number(e.target.value))}
                                >
                                    {[0, 1, 2, 3].map((n) => (
                                        <MenuItem key={n} value={n}>
                                            Ligne {n + 1}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    </Box>

                    <Typography variant="h6">Mapping des colonnes</Typography>
                    <Table sx={{ mt: 1 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Champ attendu</TableCell>
                                <TableCell>Colonne du fichier</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {expectedFields.map((field) => (
                                <TableRow key={field}>
                                    <TableCell>{field}</TableCell>
                                    <TableCell>
                                        <Select
                                            value={mapping[field] ?? ""}
                                            onChange={(e) =>
                                                setMapping((prev) => ({ ...prev, [field]: e.target.value }))
                                            }
                                            displayEmpty
                                            fullWidth
                                            size="small"
                                        >
                                            <MenuItem value="">
                                                <em>Non utilisé</em>
                                            </MenuItem>
                                            {headerLabels.map((label, i) => (
                                                <MenuItem key={i} value={i}>
                                                    {label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>

                    <Button variant="contained" sx={{ mt: 2 }} onClick={applyMapping}>
                        Prévisualiser
                    </Button>
                </Box>
            )}

            {error && (
                <Typography color="error" sx={{ mt: 2 }}>
                    {error}
                </Typography>
            )}

            {mappedData.length > 0 && (
                <Box sx={{ mt: 3 }}>
                    <Typography variant="h6">Prévisualisation ({mappedData.length} lignes)</Typography>
                    <Table sx={{ mt: 1 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Fiscode</TableCell>
                                <TableCell>Nom</TableCell>
                                <TableCell>Année</TableCell>
                                <TableCell>Sexe</TableCell>
                                <TableCell>Nation</TableCell>
                                <TableCell>Club</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {mappedData.slice(0, 10).map((a, i) => (
                                <TableRow key={i}>
                                    <TableCell>{a.fiscode}</TableCell>
                                    <TableCell>{a.name}</TableCell>
                                    <TableCell>{a.birthyear}</TableCell>
                                    <TableCell>{a.gender}</TableCell>
                                    <TableCell>{a.nation}</TableCell>
                                    <TableCell>{a.club}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>

                    <Button variant="contained" sx={{ mt: 2 }} onClick={sendToBackend}>
                        Envoyer au backend ({mappedData.length} athlètes)
                    </Button>
                </Box>
            )}

            {athletes.length > 0 && (
                <Box sx={{ mt: 4 }}>
                    <Typography variant="h6" color="success.main">
                        Import réussi — {athletes.length} athlètes
                    </Typography>
                </Box>
            )}
        </Box>
    );
}